import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { holidayApi, F90H, HOLIDAY_TYPES } from '../../../services/holidayApi';
import dayjs from 'dayjs';

export type Mode = 'browse' | 'add' | 'edit';

const emptyForm = () => ({
  f90h_01: '',
  f90h_11: '',
  f90h_12: 1,
  f90h_13: '',
  f90h_14: '',
  f90h_15: '',
  f90h_21: '',
  f90h_22: '',
  f90h_23: '',
  f90h_24: '',
});

export function useHolidayForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F90H[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [form, setForm] = useState(emptyForm());

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Helper to format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return dayjs(dateStr).format('YYYY-MM-DD');
  };

  // Helper to get day of week
  const getDayOfWeek = (dateStr: string) => {
    if (!dateStr) return '';
    const days = ['日', '一', '二', '三', '四', '五', '六'];
    const d = dayjs(dateStr);
    return `星期${days[d.day()]}`;
  };

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await holidayApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setForm({
        f90h_01: formatDate(current.f90h_01),
        f90h_11: current.f90h_11,
        f90h_12: current.f90h_12,
        f90h_13: current.f90h_13,
        f90h_14: current.f90h_14,
        f90h_15: current.f90h_15,
        f90h_21: current.f90h_21,
        f90h_22: current.f90h_22,
        f90h_23: current.f90h_23,
        f90h_24: current.f90h_24,
      });
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setForm(emptyForm());
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setForm({
      f90h_01: formatDate(current.f90h_01),
      f90h_11: current.f90h_11,
      f90h_12: current.f90h_12,
      f90h_13: current.f90h_13,
      f90h_14: current.f90h_14,
      f90h_15: current.f90h_15,
      f90h_21: current.f90h_21,
      f90h_22: current.f90h_22,
      f90h_23: current.f90h_23,
      f90h_24: current.f90h_24,
    });
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setForm({
        f90h_01: formatDate(current.f90h_01),
        f90h_11: current.f90h_11,
        f90h_12: current.f90h_12,
        f90h_13: current.f90h_13,
        f90h_14: current.f90h_14,
        f90h_15: current.f90h_15,
        f90h_21: current.f90h_21,
        f90h_22: current.f90h_22,
        f90h_23: current.f90h_23,
        f90h_24: current.f90h_24,
      });
    }
  };

  // Update a single field
  const setField = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await holidayApi.create({
          f90h_01: form.f90h_01,
          f90h_11: form.f90h_11,
          f90h_12: form.f90h_12,
          f90h_13: form.f90h_13,
          f90h_14: form.f90h_14,
          f90h_15: form.f90h_15,
          f90h_21: form.f90h_21,
          f90h_22: form.f90h_22,
          f90h_23: form.f90h_23,
          f90h_24: form.f90h_24,
        });
        message.success('新增成功');
      } else {
        await holidayApi.update(form.f90h_01, {
          f90h_11: form.f90h_11,
          f90h_12: form.f90h_12,
          f90h_13: form.f90h_13,
          f90h_14: form.f90h_14,
          f90h_15: form.f90h_15,
          f90h_21: form.f90h_21,
          f90h_22: form.f90h_22,
          f90h_23: form.f90h_23,
          f90h_24: form.f90h_24,
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const idx = data.findIndex(r => formatDate(r.f90h_01) === form.f90h_01);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await holidayApi.delete(formatDate(current.f90h_01));
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r =>
      r.f90h_11.includes(searchText) || formatDate(r.f90h_01).includes(searchText)
    );
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    form, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
    formatDate, getDayOfWeek,
    HOLIDAY_TYPES,
  };
}
